import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { AlertTriangle, Bell, ChevronRight } from "lucide-react";
import type { ManualTask, RoleTask } from "@arc/contracts";
import { Button } from "@/components/ui/button";
import { ManualIcon } from "@/components/icons";
import { Disclosure } from "@/components/ui/disclosure";
import { cn } from "@/lib/utils";
import type { AuthSession } from "../../../auth/keycloak.js";
import { useFacilities } from "../../facilities/useFacilities.js";
import { facilityPathLabel } from "../../facilities/facility-tree.js";
import { useFacilityNavParams } from "../../facilities/drilldown/useFacilityNavParams.js";
import { useDashboardTasks } from "../useDashboardTasks.js";
import { manualTaskTexts, roleTaskTexts, type TaskTexts } from "./task-texts.js";

type Task = { key: string; kind: "hint" | "warning"; texts: TaskTexts; manual: ManualTask | null; role: RoleTask | null };
/** A group: the role warnings ("General") or one facility — keyed by ID, labelled by its path (names repeat across a tree; diff panel). */
type Group = { key: string; label: string; listLabel: string; tasks: Task[] };

/**
 * The legacy Aufgaben page (ADR-034 D4): the counters (hints = bell, warnings = triangle), the rows grouped by facility
 * — warnings first — with the role warnings in a "General" group, and the selected row's detail: Information ·
 * Troubleshooting options · Interactions (Open manual / Open facility / Manage roles). The rows are the SAME the
 * Übersicht counts (one engine, one read family).
 */
export function TasksPage({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  const nav = useFacilityNavParams();
  const tasks = useDashboardTasks(session);
  const facilities = useFacilities(session);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  // Facility labels from the caller's tree ("Site / Line / Machine" — names repeat across a tree); the row's own
  // facility name when the tree does not know it (an archived one, or the tree still loading).
  const byId = useMemo(() => new Map((facilities.data ?? []).map((f) => [f.id, { name: f.name, parentId: f.parentId }] as const)), [facilities.data]);
  const groups = useMemo<Group[]>(() => {
    if (!tasks.data) return [];
    const byFacility = new Map<number, Group>();
    const roleGroup: Task[] = tasks.data.roles.map((r) => ({ key: `role-${String(r.roleId)}-${r.type}`, kind: "warning", texts: roleTaskTexts(r, t), manual: null, role: r }));
    for (const m of tasks.data.manuals) {
      const node = byId.get(m.facilityId);
      const ancestors = node ? facilityPathLabel(byId, m.facilityId) : "";
      const label = node ? (ancestors ? `${ancestors} / ${node.name}` : node.name) : m.facilityName;
      const g = byFacility.get(m.facilityId) ?? { key: `facility-${String(m.facilityId)}`, label, listLabel: t("dashboard.tasksIn", { label }), tasks: [] };
      g.tasks.push({ key: `manual-${String(m.manualId)}-${m.type}`, kind: m.kind, texts: manualTaskTexts(m, t), manual: m, role: null });
      byFacility.set(m.facilityId, g);
    }
    const facilityGroups = [...byFacility.values()].sort((a, b) => a.label.localeCompare(b.label) || a.key.localeCompare(b.key));
    for (const g of facilityGroups) g.tasks.sort((a, b) => (a.kind === b.kind ? 0 : a.kind === "warning" ? -1 : 1)); // warnings first, else the server's order
    return roleGroup.length > 0 ? [{ key: "general", label: t("dashboard.general"), listLabel: t("dashboard.roleWarnings"), tasks: roleGroup }, ...facilityGroups] : facilityGroups;
  }, [tasks.data, byId]);
  const all = useMemo(() => groups.flatMap((g) => g.tasks), [groups]);
  const selected = all.find((t) => t.key === selectedKey) ?? null;
  const hints = all.filter((t) => t.kind === "hint").length;
  const warnings = all.length - hints;

  return (
    <div className="flex flex-col gap-4">
      <dl className="m-0 flex gap-6 text-sm">
        <div className="flex items-center gap-2">
          <dd className="m-0 flex items-center gap-1 text-2xl font-semibold tabular-nums">
            {hints} <Bell aria-hidden="true" className="text-muted-foreground size-4" />
          </dd>
          <dt className="text-muted-foreground">{t("dashboard.hintUnit", { count: hints })}</dt>
        </div>
        <div className="flex items-center gap-2">
          <dd className="m-0 flex items-center gap-1 text-2xl font-semibold tabular-nums">
            {warnings} <AlertTriangle aria-hidden="true" className="text-attention size-4" />
          </dd>
          <dt className="text-muted-foreground">{t("dashboard.warningUnit", { count: warnings })}</dt>
        </div>
      </dl>
      {tasks.isPending ? (
        <p role="status" className="text-muted-foreground text-sm">
          {t("other.loading")}
        </p>
      ) : tasks.isError ? (
        <div className="flex flex-col items-start gap-2">
          <p role="alert" className="text-destructive text-sm">
            {t("dashboard.couldntLoadTheTasks")}
          </p>
          <Button variant="outline" size="sm" onPress={() => void tasks.refetch()}>
            {t("dashboard.tryAgain")}
          </Button>
        </div>
      ) : all.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t("dashboard.noTasks")}</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,26rem)]">
          <div className="flex flex-col gap-2">
            {groups.map((g) => (
              <Disclosure key={g.key} defaultExpanded title={<span className="truncate">{g.label}</span>}>
                <ul aria-label={g.listLabel} className="m-0 flex list-none flex-col gap-1 p-0">
                  {g.tasks.map((task) => (
                    <li key={task.key}>
                      <button
                        type="button"
                        aria-pressed={task.key === selectedKey}
                        onClick={() => setSelectedKey(task.key === selectedKey ? null : task.key)}
                        className={cn(
                          "hover:bg-muted focus-visible:ring-ring/50 flex w-full cursor-pointer items-start gap-2 rounded-md border-0 bg-transparent px-2 py-1.5 text-left text-sm outline-none focus-visible:ring-2",
                          task.key === selectedKey && "bg-muted",
                        )}
                      >
                        {task.kind === "warning" ? <AlertTriangle aria-label={t("dashboard.warning")} className="text-attention mt-0.5 size-4 shrink-0" /> : <Bell aria-label={t("dashboard.hint")} className="text-muted-foreground mt-0.5 size-4 shrink-0" />}
                        <span>
                          <strong>{task.texts.subject}</strong> {task.texts.short}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </Disclosure>
            ))}
          </div>
          <section aria-label={t("dashboard.taskDetails")} className="border-border bg-muted/30 flex flex-col gap-3 rounded-lg border p-4 text-sm">
            {selected === null ? (
              <p className="text-muted-foreground m-0">{t("dashboard.noManualSelectedText")}</p>
            ) : (
              <>
                <h2 className="m-0 text-base font-semibold">{selected.texts.subject}</h2>
                <div>
                  <h3 className="text-muted-foreground m-0 text-xs font-medium uppercase">{t("dashboard.infoHeading")}</h3>
                  <p className="m-0 mt-1">{selected.texts.info}</p>
                </div>
                <div>
                  <h3 className="text-muted-foreground m-0 text-xs font-medium uppercase">{t("dashboard.solveHeading")}</h3>
                  <p className="m-0 mt-1">{selected.texts.solve}</p>
                </div>
                <div>
                  <h3 className="text-muted-foreground m-0 text-xs font-medium uppercase">{t("dashboard.interactionsHeading")}</h3>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {selected.manual ? (
                      <>
                        <Button variant="accent" className="h-8 rounded-[10px] px-3 text-[13px]" onPress={() => nav.openManualDetails(selected.manual!.facilityId, selected.manual!.manualId)}>
                          <ManualIcon aria-hidden="true" />
                          {t("dashboard.openManual")}
                        </Button>
                        <Button variant="outline" size="sm" onPress={() => nav.openManuals(selected.manual!.facilityId)}>
                          {t("facilitySelection.openFacility")}
                          <ChevronRight aria-hidden="true" />
                        </Button>
                      </>
                    ) : null}
                    {selected.role ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onPress={() => {
                          nav.openView("roles", true);
                          nav.selectRole(selected.role!.roleId);
                        }}
                      >
                        {t("dashboard.roleManagement")}
                      </Button>
                    ) : null}
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
